export function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

export function getEffectBoxStyle(effect) {
  if (effect.hidden) {
    return {
      display: 'none'
    };
  }
  const ret = {};
  ['top', 'left', 'width', 'height'].forEach(s => {
    ret[s] = effect.content[s] + 'px';
  });
  if (effect.content.rotate) {
    ret.transform = `rotate(${effect.content.rotate}deg)`;
  }
  return ret;
}

/**
 * do something
 * @param {{content: {}, enter: string, leave: string, enterDuration: number, leaveDuration: number}} effect
 * @param {number} current the current progress relative to fragment start.
 */
export function getEffectContentStyle(effect, current) {
  const ret = getEffectBoxStyle(effect);
  const { opacity, brightness, flip } = effect.content || {};
  const usedOpacity = typeof opacity === 'undefined' ? 1 : opacity / 100;
  ret.opacity = usedOpacity;
  if (typeof brightness !== undefined) {
    ret.filter = `brightness(${brightness})`;
  }
  if (effect.content.scale !== undefined) {
    ret.transform = (ret.transform ? ret.transform : '') + ` scale(${effect.content.scale})`;
  }
  switch (flip) {
    case 'horizontal,vertical':
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleX(-1)';
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleY(-1)';
      break;
    case 'vertical,horizontal':
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleY(-1)';
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleX(-1)';
      break;
    case 'horizontal':
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleX(-1)';
      break;
    case 'vertical':
      ret.transform = (ret.transform ? ret.transform : '') + ' scaleY(-1)';
      break;
  }

  ret.transform = (ret.transform ? ret.transform : '') + 'translateZ(0)';

  if (typeof current !== 'undefined') {
    const startOffset = current - effect.start;
    if (startOffset >= 0 && startOffset <= effect.duration) {
      if (effect.enter !== 'none' && startOffset < effect.enterDuration) {
        const enterProgress = startOffset / effect.enterDuration;
        ret.opacity = usedOpacity * enterProgress;
      }

      if (effect.leave !== 'none' && startOffset > effect.duration - effect.leaveDuration) {
        const leaveProgress = (startOffset - effect.duration) / effect.leaveDuration + 1;
        ret.opacity = usedOpacity * (1 - leaveProgress);
      }
    }
  }
  return ret;
}

/**
 * Rotate `point` around `ref` by `a` degrees clockwise. Returns the rotated point in CSS coordinates.
 * @param {{x:number,y:number}} point the CSS coordinates of the rotated point in the format of {x, y}.
 * @param {{x:number,y:number}} ref the CSS coordinates of the center of rotation in the format of {x, y}.
 * @param {number} a the angle of rotation in degrees.
 */
export const rotateBy = (point, ref, a) => {
  const deltaX = point.x - ref.x;
  const deltaY = point.y - ref.y;
  const usedAngle = a * Math.PI / 180;
  const sinVal = Math.sin(usedAngle);
  const cosVal = Math.cos(usedAngle);

  return {
    x: ref.x + cosVal * deltaX - sinVal * deltaY,
    y: ref.y + sinVal * deltaX + cosVal * deltaY
  };
};

export function rotateRect(efc) {
  const center = { x: efc.left + efc.width / 2, y: efc.top + efc.height / 2 };
  return [
    { x: efc.left, y: efc.top },
    { x: efc.left + efc.width, y: efc.top },
    { x: efc.left + efc.width, y: efc.top + efc.height },
    { x: efc.left, y: efc.top + efc.height }
  ].map(p => rotateBy(p, center, efc.rotate));
}

export const encodeURIIfNeeded = (url) => {
  if (/\s/.test(url)) {
    return encodeURI(url);
  }
  return url;
};

export function getImageAspectRatio(url, done, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    if (crossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      document.body.removeChild(img);
      if (done) {
        done(img);
      }
      resolve(img.naturalWidth / img.naturalHeight);
    };
    img.onerror = () => {
      reject(new Error('Image loading failed.'));
    };
    img.src = encodeURIIfNeeded(url);
    img.style.display = 'none';
    document.body.appendChild(img);
  });
}

export function getVideoAspectRatio(url, done) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.onloadedmetadata = () => {
      if (done) {
        done(video);
      }
      document.body.removeChild(video);
      resolve(video.videoWidth / video.videoHeight);
    };
    video.onerror = () => {
      reject(new Error('Video loading failed.'));
    };
    video.src = encodeURIIfNeeded(url);
    video.style.display = 'none';
    document.body.appendChild(video);
  });
}

const videoDurationLookup = {};

export function getVideoDuration(url) {
  const usedURL = encodeURIIfNeeded(url);
  return new Promise((resolve, reject) => {
    if (usedURL in videoDurationLookup) {
      resolve(videoDurationLookup[usedURL]);
    }
    const video = document.createElement('video');
    video.onloadedmetadata = () => {
      document.body.removeChild(video);
      videoDurationLookup[usedURL] = video.duration;
      resolve(video.duration);
    };
    video.onerror = () => {
      reject(new Error('Video loading failed.'));
    };
    video.src = usedURL;
    video.style.display = 'none';
    document.body.appendChild(video);
  });
}

export function getAudioDuration(url) {
  console.log('getAudioDuration')
  const usedURL = encodeURIIfNeeded(url);
  return new Promise((resolve, reject) => {
    if (usedURL in videoDurationLookup) {
      resolve(videoDurationLookup[usedURL]);
    }
    const audio = document.createElement('audio');
    // do we still need to append it to body?
    audio.onloadedmetadata = () => {
      videoDurationLookup[usedURL] = audio.duration;
      resolve(audio.duration);
    };
    audio.onerror = () => {
      reject(new Error('Video loading failed.'));
    };
    audio.src = usedURL;
  });
}

export function formatTimeInterval(value) {
  const roundedValue = Math.round(value / 10) * 10;
  const milliseconds = roundedValue % 1e3;
  let seconds = Math.floor(roundedValue / 1e3);
  let minutes = Math.floor(seconds / 60);
  seconds %= 60;

  return [
    ('0' + minutes).slice(-2),
    ('0' + seconds).slice(-2)
  ].join(':') + '.' + ('00' + milliseconds).slice(-3, -1);
}

export const isMac = /mac/i.test(navigator.platform); // TODO:

export function getAngle({ x, y }) {
  return (y > 0 ? 1 : -1) * Math.acos(x / Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)));
}

export function arcAt(orig, p1, p2, r, origMax) {
  const l1 = { x: p1.x - orig.x, y: p1.y - orig.y };
  const a1 = getAngle(l1);
  const l2 = { x: p2.x - orig.x, y: p2.y - orig.y };
  const a2 = getAngle(l2);

  const angle = a2 - a1;
  if (!angle) {
    console.log('Invalid angle');
    return {
      intersection: [orig, orig],
      angle: 0
    }
  }
  let sideDist = Math.abs(r / Math.tan(angle / 2));

  const max = typeof origMax === 'undefined' ? Math.min.apply(Math, [l1, l2].map(({ x, y }) => Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)))) / 2 : origMax;

  if (sideDist > max) {
    sideDist = max;
  }

  return {
    intersection: [a1, a2].map(a => rotateBy({ x: orig.x + sideDist, y: orig.y }, orig, a * 180 / Math.PI)),
    counterclockwise: Math.sin(angle) > 0,
    angle: 180 - angle * 180 / Math.PI
  };
}

export function connectPointsWithRadius(pointCoords, radius) {
  return pointCoords.map((point, i) => {
    const { x, y } = point;
    if (radius) {
      const prevPoint = pointCoords[(i - 1 + pointCoords.length) % pointCoords.length];
      const nextPoint = pointCoords[(i + 1) % pointCoords.length];
      const { intersection: [first, second], counterclockwise } = arcAt(point, prevPoint, nextPoint, radius);
      return `${i === 0 ? 'M' : 'L'}${first.x} ${first.y}A${radius} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x} ${second.y}`;
    }
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ') + 'z';
}

// 字幕过滤备注内容
export const ttsFilterRemark = (str) => {
  // 中文括号转英文括号，再过滤英文括号内的内容
  return str ? str.replace(/（/g, "(").replace(/）/g, ")").replace(/\([^)]*\)/g, "") : str
};

const between = (val, start, end) => (val - start) * (val - end) <= 0;
export const betweenDelta = (val, start, delta) => between(val, start, start + delta);